import { Note02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { useRef } from "react";
import { ActivityNote } from "shared/app/Notes/ActivityNote";
import { NoteForm } from "shared/app/Notes/components/NoteForm";
import { isUnread } from "shared/app/Notes/helpers";
import { TimeLine, TimeLineItem } from "shared/components/TimeLine";
import { useNotesController } from "shared/queries/useNotesController";
import { Application } from "types/application";
import { useAuthContext } from "providers/Auth";
import { HugeiconsIcon } from "@hugeicons/react";
import { DateTime } from "luxon";
import { NotesTabs } from "shared/app/Notes/components/NotesTabs";
import { AutoLoadMoreOnScroll } from "shared/app/Notes/components/AutoLoadMoreOnScroll";

export const NotesTab = ({
  application,
  notesController
}: {
  application: Application;
  notesController: ReturnType<typeof useNotesController>;
}) => {
  const { auth } = useAuthContext();

  const scrollRootRef = useRef<HTMLDivElement | null>(null);

  return (
    <div className="h-full w-full p-6 overflow-hidden">
      <div className="flex flex-col h-full bg-content-1000-inverted rounded-lg border border-alpha-100">
        <div className="flex items-center justify-between border-b border-alpha-80 p-6">
          <p className="text-xl font-bold text-content-1000">Notes</p>
        </div>
        <div className="p-6 border-b">
          <NoteForm
            onSubmit={async (data) => {
              await notesController.addNoteMeta.mutateAsync(data);
            }}
            applicationId={String(application.id)}
            propertyName={application.property.name}
            isLoading={notesController.addNoteMeta.isPending}
            editorContainerClassName="min-h-[154px]"
          />
        </div>

        <div className="px-6 pt-6">
          <NotesTabs
            notesController={notesController}
            tabClassName="!ml-0 mr-6"
          />
        </div>
        <div
          ref={scrollRootRef}
          className="size-full flex-1 overflow-y-auto overflow-x-hidden px-6"
        >
          <TimeLine
            leftSlotWidth="142px"
            wrapperClassName="border-alpha-100 pt-1 w-full"
          >
            {notesController.showingNotes.map((note) => {
              return (
                <TimeLineItem
                  key={note.updated_at + note.created_at}
                  leftSlot={
                    <div className="pr-8">
                      <div className="flex flex-col text-sm text-right">
                        <span className="font-semibold text-content-1000">
                          {DateTime.fromISO(note.created_at).toFormat(
                            "MMM dd, yyyy"
                          )}
                        </span>
                        <span className="text-content-300">
                          {DateTime.fromISO(note.created_at).toFormat("h:mm a")}
                        </span>
                      </div>
                    </div>
                  }
                  bullet={
                    <div className="-mt-2 h-12 w-10 flex items-center justify-center bg-content-1000-inverted -translate-x-5">
                      <div className="relative bg-background-900 h-10 min-w-10 rounded-full flex items-center justify-center">
                        <HugeiconsIcon
                          icon={Note02Icon}
                          className="text-content-800"
                          strokeWidth={1.5}
                        />
                        {auth?.id != null && isUnread(note, auth.id) ? (
                          <span className="absolute -top-[3px] right-0 size-3 bg-primary-500 rounded-full ring-2 ring-alpha-40" />
                        ) : null}
                      </div>
                    </div>
                  }
                  wrapperClassName="mt-8 h-full"
                  childrenClassName="w-full"
                >
                  <ActivityNote
                    propertyName={application.property.name}
                    applicationId={String(application.id)}
                    note={note}
                    deleteNoteMeta={notesController.deleteNoteMeta}
                    editNoteMeta={notesController.editNoteMeta}
                  />
                </TimeLineItem>
              );
            })}
          </TimeLine>
          <AutoLoadMoreOnScroll
            rootRef={scrollRootRef}
            hasNextPage={Boolean(notesController.notesMeta.hasNextPage)}
            isFetchingNextPage={notesController.notesMeta.isFetchingNextPage}
            onLoadMore={() => notesController.notesMeta.fetchNextPage()}
          />
        </div>
      </div>
    </div>
  );
};
